import { Paths } from '../../../Routes/routePaths';
import { anchorForApi } from './utils';

const ONBOARDING_STEPS = ['customer_details', 'licences', 'administrator', 'projects', 'allocation', 'users', 'assignment', 'review'];

const withSearch = (pathname, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return { pathname, search: query ? `?${query}` : '' };
};

/**
 * Turns a navigation target (from the API or from a fallback) into a router location.
 * Only known pages are accepted so the API can never send the browser to an arbitrary URL.
 */
export const routeForTarget = (target) => {
  if (!target || typeof target !== 'object') return null;
  switch (target.page) {
    case 'onboarding': {
      if (!target.customer_id) return null;
      const step = ONBOARDING_STEPS.includes(target.step) ? target.step : undefined;
      return withSearch(`/flowcustomer/${encodeURIComponent(target.customer_id)}`, { step, project_id: target.project_id });
    }
    case 'project':
      return target.project_id ? {
        pathname: Paths.SuperadminProjectInsights.replace(':projectId', encodeURIComponent(target.project_id)),
        hash: target.section ? `#${encodeURIComponent(target.section)}` : '',
      } : null;
    case 'customer':
      return target.customer_id ? withSearch(
        Paths.SuperadminCustomerInsights.replace(':customerId', encodeURIComponent(target.customer_id))
      ) : null;
    case 'customers':
      return withSearch(Paths.Customers, { search: target.search });
    case 'users':
      return withSearch(Paths.Users, { search: target.search });
    case 'user':
      return target.user_id ? withSearch(`${Paths.EditUsers}/${encodeURIComponent(target.user_id)}`) : null;
    case 'course_requests':
      return withSearch(Paths.CourseRequests, { search: target.search });
    case 'reporting':
      return withSearch(Paths.Reporting);
    default:
      return null;
  }
};

/**
 * What each attention item links to when the API has not sent its affected records.
 * `recordStep` is the onboarding step that fixes the problem for one project or customer.
 */
export const ATTENTION_FALLBACKS = {
  over_assigned_licences: { recordStep: 'allocation', recordAction: 'Open licence allocation', destination: { page: 'customers' }, destinationLabel: 'Open customer list' },
  stalled_seats: { recordStep: 'assignment', recordAction: 'Open licence assignment', destination: { page: 'reporting' }, destinationLabel: 'Open reporting' },
  idle_projects: { recordStep: 'assignment', recordAction: 'Assign learners', destination: { page: 'customers' }, destinationLabel: 'Open customer list' },
  overdue_projects: { recordStep: 'projects', recordAction: 'Edit project', destination: { page: 'customers' }, destinationLabel: 'Open customer list' },
  learners_not_in_project: { recordAction: 'Open learner', destination: { page: 'users' }, destinationLabel: 'Open user list' },
  customers_without_projects: { recordStep: 'projects', recordAction: 'Create a project', destination: { page: 'customers' }, destinationLabel: 'Open customer list' },
  pending_course_requests: { recordAction: 'Review request', destination: { page: 'course_requests' }, destinationLabel: 'Open course requests' },
  onboarding_stuck: { recordStep: 'review', recordAction: 'Continue onboarding', destination: { page: 'customers' }, destinationLabel: 'Open customer list' },
  sync_late: { anchor: 'sync_health' },
  impersonation_open: { anchor: 'impersonation' },
  learning_hub_plans_ending: { anchor: 'learning_hub' },
};

const fallbackFor = (item) => ATTENTION_FALLBACKS[item?.key] || {};

/** Dashboard section an attention item points at, e.g. `GET /main/superadmin_insights (sync_health)`. */
export const anchorForAttention = (item) => anchorForApi(item?.action?.api) || fallbackFor(item).anchor || null;

/** Where one affected record should open. API-provided targets win over the key-based default. */
export const routeForRecord = (item, record) => {
  if (!record) return null;
  const explicit = routeForTarget(record.target);
  if (explicit) return explicit;
  const fallback = fallbackFor(item);
  if (record.customer_id) {
    return routeForTarget({ page: 'onboarding', customer_id: record.customer_id, project_id: record.project_id, step: fallback.recordStep });
  }
  if (record.user_id) return routeForTarget({ page: 'user', user_id: record.user_id });
  return null;
};

/** The "see everything" link for an attention item. */
export const destinationForItem = (item) => {
  const fallback = fallbackFor(item);
  const target = item?.action?.target || fallback.destination;
  return {
    route: routeForTarget(target),
    label: item?.action?.target_label || fallback.destinationLabel || 'Open list',
  };
};

export const recordActionLabel = (item, record) => record?.action_label || fallbackFor(item).recordAction || 'Open';
